/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import Async from 'react-select';
import 'react-select/dist/react-select.css';
import { jobModes, serviceTypes, vehicleTypes } from '../../../consts';
import SelectExtraSkillsComponent from './SelectExtraSkillsComponent';
import { TServiceKey, TServicesStructure } from '../../../types';

type TSelectedOption = {
    value: string;
    label: string;
    iconName: string
}

type TSelectedRole = {
    name: string;
    value: string;
    label: string
}

type TSelectRoleComponent = {
    servicesStructure: TServicesStructure;
    serviceKey: TServiceKey;
    roleData: any;
    update: (selectedRole: TSelectedRole) => void;
    disabled: boolean;
    mode: string;
    extraDriverDetail: any;
    extraDriverSkills: any;
    extraSkillsDetailGeneral: any;
    displayRoleIcon?: boolean;
    isAgent: boolean;
};

export default function SelectRoleComponent({
    servicesStructure,
    serviceKey,
    roleData,
    update,
    disabled,
    mode,
    extraDriverDetail,
    extraDriverSkills,
    extraSkillsDetailGeneral,
    displayRoleIcon = true,
    isAgent,
}: TSelectRoleComponent) {
    const [selectedOption, setSelectedOption] = useState(null);
    const [roles, setRoles] = useState([]);
    const [serviceRoleName, setServiceRoleName] = useState('');
    const [serviceRolesLength, setServiceRolesLength] = useState(0);

    const getServiceRolesLength = () => {
        if (servicesStructure && serviceRoleName) {
            let serviceRoleNameValue;
            if (serviceRoleName === 'vehicle_type') {
                serviceRoleNameValue = String(roleData[serviceKey].vehicle_type);
            } else {
                serviceRoleNameValue = roleData[serviceKey].role;
            }
            const serviceFieldName = servicesStructure[serviceKey]?.role_filter_name;
            const serviceTypeData = servicesStructure[serviceKey]?.[serviceFieldName][serviceRoleNameValue];
            if (serviceTypeData && typeof serviceTypeData === 'object') {
                const extraSkillsNumber = Object.keys(serviceTypeData).filter(key =>
                    serviceTypeData[key] && typeof serviceTypeData[key] === 'object').length;
                setServiceRolesLength(extraSkillsNumber);
            }
        } else {
            const extraSkillsNumber = 0;
            setServiceRolesLength(extraSkillsNumber);
        }
    };

    const handleSelectChange = (selectedRole: any) => {
        if (selectedRole) {
            setSelectedOption({
                label: selectedRole.currentTarget.label,
                value: selectedRole.currentTarget.value,
                iconName: selectedRole.currentTarget.iconName,
            });
        } else {
            setSelectedOption('');
        }
        update(selectedRole);
    };

    const setDefaultRole = (options: any[]) => {
        const fieldName = servicesStructure[serviceKey].role_field_name;
        const defaultRoleValue = roleData[serviceKey][fieldName];

        if (defaultRoleValue) {
            // eslint-disable-next-line eqeqeq
            const defaultRole = options.find(role => role.value == defaultRoleValue) || roles[0];
            if (defaultRole) {
                const serviceKeyData = servicesStructure[serviceKey];
                const roleFieldName = serviceKeyData?.role_field_name;
                const simulatedEvent = {
                    currentTarget: {
                        name: roleFieldName,
                        value: defaultRole.value,
                        label: defaultRole.label,
                        iconName: defaultRole.iconName,
                    },
                };
                handleSelectChange(simulatedEvent);
            }
        }
    };

    const setOptionsFromStructure = (structure: TServicesStructure) => {
        if (structure) {
            const serviceKeyData = servicesStructure[serviceKey];
            const roleFilterName = serviceKeyData?.role_filter_name;
            const roleFieldName = serviceKeyData?.role_field_name;
            const rolesData = serviceKeyData?.[roleFilterName] || {};

            // Filter out the roles that we don't want to include in the options
            const filteredRoles = Object.entries(rolesData).filter(([key, value]) =>
                key &&  // filter out empty keys
                (isAgent || value.client_enabled !== false) &&  // filter out disabled roles if not agent
                ((+roleData[serviceKey][roleFilterName] === vehicleTypes.MOTORCYCLE && mode === jobModes.EDIT)
                || value.label !== 'Motorcycle / Scooter') // filter out motorcycle if not in edit motorcycle job mode
            );
            const options = filteredRoles.map(([key, value]) => ({
                label: value.label,
                value: key,
                iconName: value.icon_name,
            }));
            setRoles(options);
            setServiceRoleName(roleFieldName);
            setDefaultRole(options);
        }
    };

    useEffect(() => {
        if (servicesStructure && serviceKey) {
            setOptionsFromStructure(servicesStructure);
        }
    }, [servicesStructure, serviceKey]);

    useEffect(() => {
        getServiceRolesLength();
    }, [selectedOption, serviceKey]);

    const renderOption = (option: TSelectedOption) => (
        <div className="role-list-container">
            <i className={`icon ${option.iconName} normal role-list-logo`}/>
            <div className="role-description-container">
                <div>{option.label} </div>
            </div>
        </div>
    );

    const arrowRender = () => (
        <div className='arrow collapsed'>
            <span className="chevron"/>
        </div>
    );

    const handleSelectWrapper = (option: TSelectedOption) => {
        if (option) {
            const event = {
                currentTarget: {
                    name: serviceRoleName,
                    value: option.value,
                    label: option.label,
                    iconName: option.iconName,
              }
            };
            handleSelectChange(event);
        }
    };

    return (
        <div className='select-role-container'>
            <form className='select-role-form'>
                <div>
                    <Async
                        className="swiper-no-swiping"
                        disabled={disabled}
                        onChange={handleSelectWrapper}
                        options={roles}
                        placeholder={`Select a ${
                            serviceKey === serviceTypes.DRIVER
                                ? 'Vehicle Type'
                                : 'Role'
                        }`}
                        simpleValue={false}
                        value={selectedOption}
                        optionRenderer={renderOption}
                        arrowRenderer={arrowRender}
                    />
                </div>
                {displayRoleIcon && (
                    <>
                        <br/>
                        <div className='role-item active'>
                            <div className="item-circle-wrapper">
                                <i className={`icon ${selectedOption?.iconName} normal`}/>
                                <i className={`icon ${selectedOption?.iconName} active`}/>
                            </div>
                            <p className="role-item-name">
                                {selectedOption?.label}
                            </p>
                        </div>
                    </>
                )}
            </form>
            {
                serviceKey !== serviceTypes.DRIVER ? (
                    <>
                        {serviceRolesLength > 3 ? (
                            // render dropdown for extra skills
                            <div>
                                <p className="question">{serviceRolesLength > 0 ? 'Any extra skill(s)?' : ''}</p>
                                <SelectExtraSkillsComponent
                                    serviceKey={serviceKey}
                                    roleData={roleData}
                                    serviceStructure={servicesStructure}
                                    selectedRole={selectedOption}
                                    // @ts-ignore
                                    update={update}
                                    disabled={false}
                                />
                            </div>
                        ) : (
                            // render icons for extra skills
                            extraSkillsDetailGeneral()
                        )}
                    </>
                ) : (
                    // render extra skills and vehicle details for driver sector
                    <>
                        {extraDriverDetail()}
                        {extraDriverSkills()}
                    </>
                )
            }
        </div>
    );
}
